// Cliente de Client Radar. Consume el proxy GATEADO de Orión
// (/orion/api/client-radar/radar/*), que exige JWT y reenvía X-Org-Id (la VM
// aísla los leads por org). Mismo patrón de auth (Bearer de localStorage) que
// los demás módulos. La UI es NATIVA del dashboard (tokens semánticos), NO el
// HTML embebido — así respeta el diseño claro/oscuro.

const ORION = '/orion/api';
const BASE = '/client-radar/radar';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText} — ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export type Vertical = {
  vertical: string;
  leads: number;
  qualified: number;
  by_source: Record<string, number>;
  by_country: Record<string, number>;
  top: Array<{ name: string; org: string; country: string; score: number }>;
};
export type Summary = { total?: number; verticals: Vertical[] };

export type Lead = {
  id: string;
  vertical: string;
  name: string;
  org: string;
  role?: string;
  country?: string;
  org_type?: string;
  score: number;
  tier?: string | null;        // A/B/C (cognición U-CogNet; null en leads viejos)
  reason?: string | null;      // por qué es prospecto
  signal?: string | null;      // qué disparó el lead
  source?: string | null;      // fuente (cordis/arxiv/pubmed/…)
  email?: string | null;
  website?: string | null;
  linkedin?: string | null;    // resuelto por el enriquecimiento gobernado
  enriched?: boolean;
  tags?: string[];
  governed?: boolean;
  n_drafts?: number;
};

export type EnrichResult = {
  website?: string | null;
  email?: string | null;
  linkedin?: string | null;
  decision_id?: string;
};

export type EnrichBatchResult = {
  scanned: number;
  enriched: number;
  with_email: number;
  with_website: number;
  with_linkedin: number;
  leads?: Lead[];
};

export type LeadDetail = {
  lead?: Lead;
  brief?: {
    profile?: string;
    why_prospect?: string;
    how_to_approach?: string;
    signals?: string;
  };
  /** Trabajos/fuentes que U-CogNet consultó (OpenAlex) para escribir el brief. */
  research?: string[];
  /** true = brief servido de caché; null/false = el modelo investigó en vivo. */
  cached?: boolean | null;
  decision_id?: string;
};

export type Draft = {
  subject?: string;
  body?: string;
  decision_id?: string;
  withheld?: boolean;
  reason?: string;   // si withheld, por qué
};

export type LeadsParams = {
  vertical?: string;
  min_score?: number;
  country?: string;
  q?: string;
  limit?: number;
};

export async function getSummary(): Promise<Summary> {
  const res = await fetch(`${ORION}${BASE}/summary`, { headers: authHeaders() });
  return asJson<Summary>(res);
}

export async function getLeads(params: LeadsParams = {}): Promise<Lead[]> {
  const qs = new URLSearchParams();
  if (params.vertical) qs.set('vertical', params.vertical);
  if (params.min_score != null) qs.set('min_score', String(params.min_score));
  if (params.country) qs.set('country', params.country);
  if (params.q) qs.set('q', params.q);
  qs.set('limit', String(params.limit ?? 100));
  const res = await fetch(`${ORION}${BASE}/leads?${qs.toString()}`, { headers: authHeaders() });
  const d = await asJson<{ leads: Lead[] }>(res);
  return d.leads ?? [];
}

/** Deep-search en lenguaje natural (~150s: 6 fuentes → leads gobernados). */
export async function search(query: string): Promise<Record<string, unknown>> {
  const res = await fetch(`${ORION}${BASE}/search`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  return asJson<Record<string, unknown>>(res);
}

/** Detalle de un lead + brief accionable (U-CogNet investiga en OpenAlex). */
export async function getLeadDetail(id: string): Promise<LeadDetail> {
  const res = await fetch(`${ORION}${BASE}/lead/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  return asJson<LeadDetail>(res);
}

/** Genera un outreach personalizado gobernado (Ethics/Constitution). */
export async function generateDraft(leadId: string): Promise<Draft> {
  const res = await fetch(`${ORION}${BASE}/draft`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ lead_id: leadId }),
  });
  return asJson<Draft>(res);
}

/** Resuelve contacto de UN lead (web propia: DuckDuckGo + fetch, gobernado). */
export async function enrich(leadId: string): Promise<EnrichResult> {
  const res = await fetch(`${ORION}${BASE}/enrich`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ lead_id: leadId }),
  });
  return asJson<EnrichResult>(res);
}

/** Enriquece un lote de leads (por score/vertical). */
export async function enrichBatch(
  limit = 25,
  opts: { min_score?: number; vertical?: string } = {},
): Promise<EnrichBatchResult> {
  const res = await fetch(`${ORION}${BASE}/enrich_batch`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit, ...opts }),
  });
  return asJson<EnrichBatchResult>(res);
}

/** Analista gobernado sobre los leads de la org. */
export async function preguntar(pregunta: string): Promise<Record<string, unknown>> {
  const res = await fetch(`${ORION}${BASE}/preguntar`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ pregunta }),
  });
  return asJson<Record<string, unknown>>(res);
}
